import { useState } from "react";
import { CameraCapture } from "@/components/CameraCapture";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, SlidersHorizontal, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";

export function PhotoDiagnosis({ meta, setMeta, language, onEstimate, onFullResult }) {
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(null);
  const [observation, setObservation] = useState(null);

  const run = async (mode) => {
    if (!photo) return toast.error("Take or upload a soil photo first");
    setBusy(mode);
    try {
      const res = await apiClient.post("/analyze/photo", { image_base64: photo.base64, mime_type: photo.mime, mode, location: meta.location, goal: meta.goal, language });
      setObservation({ text: res.data.photo_observations, confidence: res.data.confidence });
      if (mode === "estimate") {
        onEstimate(res.data.soil);
        toast.success("Soil values estimated from your photo — check the sliders");
      } else {
        onFullResult(res.data);
        toast.success("Photo diagnosis complete!");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8" data-testid="photo-diagnosis">
      <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Photo of your soil</Label>
      <p className="text-sm text-stone-500 mb-4">Point the camera at bare ground in daylight — a handful of dug-up soil works best.</p>
      <CameraCapture photo={photo} onPhoto={(p) => { setPhoto(p); setObservation(null); }} />

      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div>
          <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Location (optional)</Label>
          <Input data-testid="photo-location-input" value={meta.location} onChange={(e) => setMeta({ ...meta, location: e.target.value })} placeholder="e.g. Punjab, India" className="mt-1.5 bg-white" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Goal / notes (optional)</Label>
          <Input data-testid="photo-goal-input" value={meta.goal} onChange={(e) => setMeta({ ...meta, goal: e.target.value })} placeholder="e.g. soil cracks when dry" className="mt-1.5 bg-white" />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button data-testid="photo-estimate-btn" onClick={() => run("estimate")} disabled={!!busy || !photo} variant="outline"
          className="h-12 px-6 rounded-xl border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#EAF6EF] font-semibold">
          {busy === "estimate" ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <SlidersHorizontal className="h-5 w-5 mr-2" />} Fill manual values from photo
        </Button>
        <Button data-testid="photo-full-btn" onClick={() => run("full")} disabled={!!busy || !photo}
          className="h-12 px-6 rounded-xl bg-[#D97706] hover:bg-[#78350F] text-white font-semibold">
          {busy === "full" ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Looking at your soil…</> : <><Sparkles className="h-5 w-5 mr-2" /> Full AI diagnosis</>}
        </Button>
      </div>

      {observation?.text && (
        <div data-testid="photo-observations" className="mt-6 bg-[#EAF6EF] rounded-xl p-4 flex gap-3">
          <Eye className="h-5 w-5 text-[#2D6A4F] shrink-0 mt-0.5" />
          <div>
            <div className="text-xs uppercase tracking-wider text-[#2D6A4F] font-semibold mb-1">What the AI sees · {observation.confidence || "—"} confidence</div>
            <p className="text-sm text-stone-700 leading-relaxed">{observation.text}</p>
          </div>
        </div>
      )}
    </div>
  );
}
